import { CharacterData, MulticlassData } from '../types/character';
import { Level } from '../types/levelUp';
import { loadClassById, getSubclassesForClass } from './classDataLoader';
import domainsData from '../data/domains.json';

// ============================================================================
// MULTICLASS HELPER FUNCTIONS
// ============================================================================

/**
 * Checks if a character has multiclassed
 *
 * @param character - The character to check
 * @returns True if the character has a multiclass, false otherwise
 *
 * @example
 * ```ts
 * if (hasMulticlass(character)) {
 *   console.log(`Multiclassed into ${character.multiclass.className}`);
 * }
 * ```
 */
export function hasMulticlass(character: CharacterData): boolean {
  return !!character.multiclass;
}

/**
 * Gets all available domains for a character (primary class + multiclass)
 *
 * @param character - The character to get domains for
 * @returns Promise resolving to array of domain objects with id and name
 *
 * @remarks
 * - Includes all domains from primary class
 * - If multiclassed, also includes the multiclass domain
 * - Removes duplicates if same domain exists in both classes
 *
 * @example
 * ```ts
 * const domains = await getAllDomains(character);
 * console.log(domains.map(d => d.name).join(', '));
 * // Output: "Arcana, Codex, Sage" (Sage from multiclass)
 * ```
 */
export async function getAllDomains(character: CharacterData): Promise<Array<{ id: string; name: string }>> {
  const primaryDomains = await getDomainsForClassName(character.class);

  if (!character.multiclass) {
    return primaryDomains;
  }

  const multiclassDomain = getDomainById(character.multiclass.domainId);

  // Combine, removing duplicates if same domain exists in both
  const allDomains = [...primaryDomains];
  if (multiclassDomain && !allDomains.some(d => d.id === multiclassDomain.id)) {
    allDomains.push(multiclassDomain);
  }

  return allDomains;
}

/**
 * Helper: Gets domains for a class name from domains.json
 */
function getDomainsForClassName(className: string): Promise<Array<{ id: string; name: string }>> {
  return Promise.resolve(
    domainsData.domains
      .filter(d => d.classes.some(c => c.toLowerCase() === className.toLowerCase()))
      .map(d => ({ id: d.id, name: d.name }))
  );
}

/**
 * Helper: Gets a domain by ID from domains.json
 */
function getDomainById(domainId: string): { id: string; name: string } | null {
  const domain = domainsData.domains.find(d => d.id.toLowerCase() === domainId.toLowerCase());
  return domain ? { id: domain.id, name: domain.name } : null;
}

/**
 * Checks if a domain is from the multiclass (not primary class)
 *
 * @param character - The character to check
 * @param domainId - The domain ID to check
 * @returns True if the domain comes from the character's multiclass
 *
 * @example
 * ```ts
 * const maxLevel = isMulticlassDomain(character, 'sage')
 *   ? Math.ceil(characterLevel / 2) // Multiclass: limited
 *   : characterLevel; // Primary: full access
 * ```
 */
export function isMulticlassDomain(
  character: CharacterData,
  domainId: string
): boolean {
  return character.multiclass?.domainId === domainId;
}

/**
 * Gets the maximum card level available for a domain
 *
 * @param character - The character to check
 * @param domainId - The domain ID to check level access for
 * @returns Maximum card level available (1-10)
 *
 * @remarks
 * - **Multiclass domains**: Limited to half character level (rounded up)
 * - **Primary class domains**: Full character level access
 *
 * @example
 * ```ts
 * // Level 6 character with Sage multiclass
 * getMaxCardLevelForDomain(character, 'sage'); // 3 (ceil(6/2))
 * getMaxCardLevelForDomain(character, 'arcana'); // 6 (primary class)
 * ```
 */
export function getMaxCardLevelForDomain(
  character: CharacterData,
  domainId: string
): number {
  const characterLevel = parseInt(character.level);
  const isMulticlass = isMulticlassDomain(character, domainId);

  return isMulticlass
    ? Math.ceil(characterLevel / 2) // Multiclass: half level rounded up
    : characterLevel; // Primary: full level
}

/**
 * Gets all classes available for multiclassing (excludes current primary class)
 * Loads domain information from domains.json as single source of truth
 */
export function getAvailableClassesForMulticlass(
  primaryClassId: string
): Array<{ id: string; name: string; description: string; domains: string[] }> {
  // Get class-to-domains mapping from domains.json
  const classToDomainsMap = domainsData.classToDomainsMap as Record<string, string[]>;

  // Build class list from the mapping
  const allClasses = Object.keys(classToDomainsMap).map(className => {
    const classId = className.toLowerCase();
    return {
      id: classId,
      name: className,
      description: `${className} class`, // Will be replaced with actual description from class data
      domains: classToDomainsMap[className]
    };
  });

  return allClasses.filter(cls => cls.id.toLowerCase() !== primaryClassId.toLowerCase());
}

/**
 * Extracts the primary class feature from class data
 * Classes have a classFeatures array, we take the first/primary one
 */
export async function extractClassFeature(classId: string): Promise<{
  name: string;
  description: string;
}> {
  const classData = await loadClassById(classId);

  if (!classData) {
    return {
      name: classId + ' Feature',
      description: 'Class feature'
    };
  }

  // Try to get the first class feature from the array
  if (classData.classFeatures && classData.classFeatures.length > 0) {
    const feature = classData.classFeatures[0];
    return {
      name: feature.name || classData.name + ' Feature',
      description: feature.description || ''
    };
  }

  // Fallback to class name if no features defined
  return {
    name: classData.name + ' Class Feature',
    description: classData.description || ''
  };
}

/**
 * Validates that a character is eligible for multiclassing
 *
 * @param character - The character to validate
 * @returns Object containing eligibility status and optional reason string
 *
 * @remarks
 * Requirements for multiclassing:
 * - Character must be level 5 or higher
 * - Character can only multiclass once (cannot multiclass twice)
 *
 * @example
 * ```ts
 * const eligibility = canMulticlass(character);
 * if (!eligibility.eligible) {
 *   console.log(eligibility.reason);
 *   // "Multiclassing is only available at level 5 or higher"
 * }
 * ```
 */
export function canMulticlass(character: CharacterData): {
  eligible: boolean;
  reason?: string;
} {
  const level = parseInt(character.level);

  // Must be at least level 5
  if (level < 5) {
    return {
      eligible: false,
      reason: 'Multiclassing is only available at level 5 or higher'
    };
  }

  // Can only multiclass once
  if (hasMulticlass(character)) {
    return {
      eligible: false,
      reason: 'You have already multiclassed (only one multiclass allowed)'
    };
  }

  return { eligible: true };
}

/**
 * Gets foundation cards for all subclasses of a given class
 * Note: Foundation "cards" are actually the foundationFeature from the subclass
 */
export async function getFoundationCardsForClass(classId: string): Promise<Array<{
  subclassId: string;
  subclassName: string;
  cardId: string;
  cardName: string;
  description: string;
}>> {
  const subclasses = await getSubclassesForClass(classId);

  return subclasses
    .map(subclass => {
      // The foundation feature IS the foundation card
      if (!subclass.foundationFeature) return null;

      return {
        subclassId: subclass.id,
        subclassName: subclass.name,
        cardId: `${subclass.id}-foundation`, // Generate ID from subclass
        cardName: Array.isArray(subclass.foundationFeature) ? subclass.foundationFeature[0]?.name || '' : '',
        description: Array.isArray(subclass.foundationFeature) ? subclass.foundationFeature[0]?.description || '' : ''
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
}

/**
 * Validates that a selected foundation card belongs to the selected class
 */
export async function validateFoundationCardSelection(
  classId: string,
  foundationCardId: string
): Promise<boolean> {
  const foundationCards = await getFoundationCardsForClass(classId);
  return foundationCards.some(fc => fc.cardId === foundationCardId);
}

/**
 * Builds complete multiclass data from selections
 *
 * @param classId - The ID of the class to multiclass into (e.g., "seraph")
 * @param domainId - The ID of the selected domain (e.g., "grace")
 * @param foundationCardId - The ID of the selected foundation card
 * @param acquiredAtLevel - The level at which multiclass was acquired (5-10)
 * @param acquiredAtTier - The tier at which multiclass was acquired (1-4)
 * @returns Promise resolving to complete MulticlassData object ready for persistence
 *
 * @remarks
 * This function:
 * - Loads the full class data
 * - Extracts the primary class feature
 * - Finds the subclass containing the foundation card
 * - Resolves all names and descriptions
 * - Returns a complete, ready-to-save multiclass data object
 *
 * @example
 * ```ts
 * const multiclassData = await buildMulticlassData(
 *   'seraph',
 *   'grace',
 *   'seraph-hopebringer-foundation',
 *   6,
 *   2
 * );
 * // Save to character.multiclass = multiclassData
 * ```
 */
export async function buildMulticlassData(
  classId: string,
  domainId: string,
  foundationCardId: string,
  acquiredAtLevel: Level,
  acquiredAtTier: 1 | 2 | 3 | 4
): Promise<MulticlassData> {
  const classData = await loadClassById(classId);
  const classFeature = await extractClassFeature(classId);
  const domain = getDomainById(domainId);
  const subclasses = await getSubclassesForClass(classId);

  // Find the subclass that contains this foundation card
  let subclassId = '';
  let subclassName = '';
  let foundationCardName = '';

  for (const subclass of subclasses) {
    // Foundation card ID is "{subclassId}-foundation"
    if (`${subclass.id}-foundation` === foundationCardId) {
      subclassId = subclass.id;
      subclassName = subclass.name;
      foundationCardName = (Array.isArray(subclass.foundationFeature) ? subclass.foundationFeature[0]?.name : '') || 'Foundation Feature';
      break;
    }
  }

  return {
    classId,
    className: classData?.name || classId,
    classFeature: classFeature.name,
    classFeatureDescription: classFeature.description,
    domainId,
    domainName: domain?.name || domainId,
    subclassId,
    subclassName,
    foundationCardId,
    foundationCardName,
    acquiredAtLevel,
    acquiredAtTier
  };
}
